import { ApolloError } from '@apollo/client';
import {
  PricingHubBlockErrors,
  PricingHubBlockLoading,
  PricingHubBlockMetrics,
  PricingHubBlockVisibility,
  PricingHubRecentChangeRow,
} from '@sales-portal/domain/pricing/screens/overview/types';
import { toPricingHubBookmarkCounts } from '@sales-portal/domain/pricing/screens/overview/blocs/utils/pricing-hub-bookmark-counts';
import { useBusinessOrganizationPricingConfig } from '@sales-portal/blocs/hooks/use-business-organization-pricing-config';
import { useMemo } from 'react';
import { usePricingBookmarksLoader } from '@sales-portal/domain/pricing/blocs/loaders/use-pricing-bookmarks-loader';
import { usePricingBookmarksStore } from '@sales-portal/domain/pricing/blocs/stores/pricing-bookmarks-store';
import { usePricingHubAgentRecsLoader } from '@sales-portal/domain/pricing/screens/overview/blocs/loaders/use-pricing-hub-agent-recs-loader';
import { usePricingHubBlocksTransformer } from '@sales-portal/domain/pricing/screens/overview/blocs/transformers/use-pricing-hub-blocks-transformer';
import { usePricingHubCostChangesLoader } from '@sales-portal/domain/pricing/screens/overview/blocs/loaders/use-pricing-hub-cost-changes-loader';
import { usePricingHubCustomersLoader } from '@sales-portal/domain/pricing/screens/overview/blocs/loaders/use-pricing-hub-customers-loader';
import { usePricingHubRecentChangesTransformer } from '@sales-portal/domain/pricing/screens/overview/blocs/transformers/use-pricing-hub-recent-changes-transformer';
import { usePricingHubRecentPriceChangesLoader } from '@sales-portal/domain/pricing/screens/overview/blocs/loaders/use-pricing-hub-recent-price-changes-loader';

interface PricingOverviewData {
  config: PricingHubBlockVisibility;
  errors: PricingHubBlockErrors;
  loading: PricingHubBlockLoading;
  metrics: PricingHubBlockMetrics;
  recentChanges: PricingHubRecentChangeRow[];
  recentChangesError?: ApolloError;
  retryRecentChanges: () => void;
}

/**
 * Everything the hub renders. Each block owns its query so a slow one (cost
 * changes and customers allow 240s) can't hold up the page — hence per-block
 * `loading`.
 *
 * The Bookmarked tile is the exception: it counts the optimistic bookmarks
 * store, like the sidebar badge, instead of the read query's rows. Bookmarking
 * writes through that store but never refetches the query, and this screen
 * stays mounted while the user bookmarks elsewhere — reading the query here
 * would leave the tile a fetch behind.
 */
export const usePricingOverviewData = (): PricingOverviewData => {
  const {
    currencyCode,
    hasBookmarkingAccess,
    hasCustomerTableAccess,
    hasPricingAgentAccess,
  } = useBusinessOrganizationPricingConfig();

  const agentRecs = usePricingHubAgentRecsLoader();
  const costChanges = usePricingHubCostChangesLoader();
  const customers = usePricingHubCustomersLoader();
  const recent = usePricingHubRecentPriceChangesLoader();

  usePricingBookmarksLoader({ revalidate: true });

  const bookmarkEntries = usePricingBookmarksStore((store) => store.bookmarks);
  const bookmarkCounts = useMemo(
    () => toPricingHubBookmarkCounts(Object.keys(bookmarkEntries)),
    [bookmarkEntries],
  );

  const metrics = usePricingHubBlocksTransformer({
    agentRecs: agentRecs.agentRecs,
    bookmarks: bookmarkCounts,
    costChanges: costChanges.costChanges,
    currencyCode,
    customers: customers.customers,
  });

  const recentChanges = usePricingHubRecentChangesTransformer(
    recent.recentChanges,
    currencyCode,
  );

  return {
    config: {
      showAgentRecs: hasPricingAgentAccess,
      // Opening Bookmarked with nothing bookmarked bounces straight back here,
      // so a card that would dead-end is not offered.
      showBookmarked: hasBookmarkingAccess && bookmarkCounts.bookmarkCount > 0,
      showCustomers: hasCustomerTableAccess,
    },
    errors: {
      agentRecs: Boolean(agentRecs.error),
      costChanges: Boolean(costChanges.error),
      customers: Boolean(customers.error),
    },
    loading: {
      agentRecs: agentRecs.isLoading,
      costChanges: costChanges.isLoading,
      customers: customers.isLoading,
      recentChanges: recent.isLoading,
    },
    metrics,
    recentChanges,
    recentChangesError: recent.error,
    retryRecentChanges: () => recent.refetch(),
  };
};
